import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLocation } from "wouter";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { BasicInfoStep } from "./BasicInfoStep";
import { DeliveryStep } from "./DeliveryStep";
import { ValueCostStep } from "./ValueCostStep";
import { SettingsStep } from "./SettingsStep";
import { useCreatePerk, useUpdatePerk } from "@/hooks/usePerks";
import { usePhotoUploadWorkflow } from "@/hooks/usePhotos";
import { PhotoParentType } from "@/types/photos";
import type { CreateCmsPerkRequest, CmsPerkResponse } from "@/types/generated/cms.generated";

const perkWizardSchema = z.object({
  title: z.string().min(3, "Title must be at least 3 characters"),
  description: z.string().optional(),
  vendorId: z.string().min(1, "Vendor is required"),
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  categoryIds: z.array(z.string()).optional(),
  locationIds: z.array(z.string()).optional(),
  imageUrl: z.string().optional(),
  isFeatured: z.boolean().default(false),
  deliveryMethod: z.enum(["PromoCode", "QrCode", "ExternalLink", "InstructionsOnly", "PinCode"]),
  promoCodePoolType: z.enum(["Shared", "Unique"]).optional(),
  sharedPromoCode: z.string().optional(),
  externalLinkUrl: z.string().optional(),
  redemptionInstructions: z.string().optional(),
  termsAndConditions: z.string().optional(),
  valueType: z.enum(["Percentage", "FixedAmount", "FreeItem"]),
  discountValue: z.number().optional().nullable(),
  currency: z.string().optional(),
  freeItemDescription: z.string().optional(),
  coinCost: z.number().min(0).default(0),
  capFrequency: z.enum(["Once", "Daily", "Weekly", "Monthly", "Yearly"]).default("Once"),
  maxRedemptionsPerEmployee: z.number().optional().nullable(),
  maxRedemptionsTotal: z.number().optional().nullable(),
  soldOutBehavior: z.enum(["ShowBadge", "HideFromListing"]).default("ShowBadge"),
  sortOrder: z.number().default(0),
});

export type PerkWizardFormData = z.infer<typeof perkWizardSchema>;

const steps = [
  { id: "basic", label: "Basic Info", description: "Title, vendor, dates" },
  { id: "delivery", label: "Delivery", description: "How perks are delivered" },
  { id: "value", label: "Value & Cost", description: "Discount type and coins" },
  { id: "settings", label: "Settings", description: "Caps and limits" },
];

interface PerkWizardProps {
  existingPerk?: CmsPerkResponse;
  defaultValues?: Partial<PerkWizardFormData>;
}

export default function PerkWizard({ existingPerk, defaultValues }: PerkWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [, navigate] = useLocation();
  const createPerk = useCreatePerk();
  const updatePerk = useUpdatePerk();
  const { uploadPhotos, isUploading } = usePhotoUploadWorkflow();

  const isEditing = !!existingPerk;

  const form = useForm<PerkWizardFormData>({
    resolver: zodResolver(perkWizardSchema),
    defaultValues: defaultValues || {
      title: "",
      description: "",
      vendorId: "",
      startDate: "",
      endDate: "",
      categoryIds: [],
      locationIds: [],
      imageUrl: "",
      isFeatured: false,
      deliveryMethod: "PromoCode",
      promoCodePoolType: "Unique",
      sharedPromoCode: "",
      externalLinkUrl: "",
      redemptionInstructions: "",
      termsAndConditions: "",
      valueType: "Percentage",
      discountValue: null,
      currency: "AED",
      freeItemDescription: "",
      coinCost: 0,
      capFrequency: "Once",
      maxRedemptionsPerEmployee: null,
      maxRedemptionsTotal: null,
      soldOutBehavior: "ShowBadge",
      sortOrder: 0,
    },
  });

  const goNext = async () => {
    // Validate current step fields before proceeding
    const fieldsPerStep: (keyof PerkWizardFormData)[][] = [
      ["title", "vendorId", "startDate", "endDate"],
      ["deliveryMethod"],
      ["valueType", "coinCost"],
      ["capFrequency", "soldOutBehavior"],
    ];

    const valid = await form.trigger(fieldsPerStep[currentStep]);
    if (valid && currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const onSubmit = async (data: PerkWizardFormData) => {
    const payload: CreateCmsPerkRequest = {
      title: data.title,
      description: data.description || undefined,
      vendorId: data.vendorId,
      startDate: new Date(data.startDate).toISOString(),
      endDate: new Date(data.endDate).toISOString(),
      categoryIds: data.categoryIds?.length ? data.categoryIds : undefined,
      locationIds: data.locationIds?.length ? data.locationIds : undefined,
      imageUrl: data.imageUrl || undefined,
      isFeatured: data.isFeatured,
      deliveryMethod: data.deliveryMethod,
      promoCodePoolType: data.deliveryMethod === "PromoCode" ? data.promoCodePoolType : undefined,
      sharedPromoCode:
        data.deliveryMethod === "PromoCode" && data.promoCodePoolType === "Shared"
          ? data.sharedPromoCode
          : undefined,
      externalLinkUrl: data.deliveryMethod === "ExternalLink" ? data.externalLinkUrl : undefined,
      redemptionInstructions: data.redemptionInstructions || undefined,
      termsAndConditions: data.termsAndConditions || undefined,
      valueType: data.valueType,
      discountValue: data.valueType !== "FreeItem" ? data.discountValue ?? undefined : undefined,
      currency: data.valueType === "FixedAmount" ? data.currency : undefined,
      freeItemDescription: data.valueType === "FreeItem" ? data.freeItemDescription : undefined,
      coinCost: data.coinCost,
      capFrequency: data.capFrequency,
      maxRedemptionsPerEmployee: data.maxRedemptionsPerEmployee ?? undefined,
      maxRedemptionsTotal: data.maxRedemptionsTotal ?? undefined,
      soldOutBehavior: data.soldOutBehavior,
      sortOrder: data.sortOrder,
    };

    try {
      if (isEditing && existingPerk?.id) {
        await updatePerk.mutateAsync({ id: existingPerk.id, data: payload });

        // Upload new photos if selected — backend resolves imageUrl from Photos table
        if (selectedPhotos.length > 0) {
          await uploadPhotos(
            PhotoParentType.Perk,
            existingPerk.id,
            selectedPhotos
          );
        }
      } else {
        // Create perk first, then upload photos with the new ID
        const createdPerk = await createPerk.mutateAsync(payload);

        if (selectedPhotos.length > 0 && createdPerk?.id) {
          await uploadPhotos(
            PhotoParentType.Perk,
            createdPerk.id,
            selectedPhotos
          );
        }
      }
      navigate("/perks");
    } catch {
      // Error handled by mutation hooks
    }
  };

  const isSubmitting = createPerk.isPending || updatePerk.isPending || isUploading;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Step indicator */}
      <nav className="mb-8">
        <ol className="flex items-center">
          {steps.map((step, index) => (
            <li key={step.id} className="flex items-center flex-1">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                    index < currentStep
                      ? "bg-primary border-primary text-primary-foreground"
                      : index === currentStep
                        ? "border-primary text-primary"
                        : "border-muted-foreground/30 text-muted-foreground/50"
                  )}
                >
                  {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <div className="hidden sm:block">
                  <p className={cn(
                    "text-sm font-medium",
                    index <= currentStep ? "text-foreground" : "text-muted-foreground/50"
                  )}>
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-4",
                    index < currentStep ? "bg-primary" : "bg-muted-foreground/20"
                  )}
                />
              )}
            </li>
          ))}
        </ol>
      </nav>

      {/* Step content */}
      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()}>
              {currentStep === 0 && (
                <BasicInfoStep
                  form={form}
                  existingPerk={existingPerk}
                  onPhotoSelect={(files) => setSelectedPhotos(files)}
                />
              )}
              {currentStep === 1 && <DeliveryStep form={form} />}
              {currentStep === 2 && <ValueCostStep form={form} />}
              {currentStep === 3 && <SettingsStep form={form} />}

              {/* Navigation buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={currentStep === 0 ? () => navigate("/perks") : goBack}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {currentStep === 0 ? "Cancel" : "Back"}
                </Button>

                {currentStep < steps.length - 1 ? (
                  <Button type="button" onClick={goNext}>
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {isEditing ? "Update Perk" : "Create Perk"}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
